/**
 * The S2 Overview's arithmetic — the pure half of the page.
 *
 * The client wrote the refresh rule twice and asked for it to be treated as the core
 * requirement: *"All metrics should refresh in real time whenever the user changes the map
 * view, applies filters, or selects specific countries, watersheds, or program areas."* So
 * every number in the insight panel is a function of (the loaded boundary payloads, the
 * current selection, the current map extent) and of nothing else — no request, no cache, no
 * stale copy of a previous answer. Putting that function here, rather than inside the
 * component that renders it, is what makes it checkable: a card reading 4 / 11 / 31 can be
 * asserted as arithmetic instead of read back out of the DOM.
 *
 * **What "counted within the current selection/extent" means**, stated once so the cards,
 * the charts and the map cannot drift apart:
 *
 * * a unit counts when it belongs to the **explicit selection** — a clicked country or
 *   watershed, or a filter — and then the extent is not consulted at all;
 * * with no selection, a unit counts when its geometry **intersects the visible map
 *   extent**. Intersects, not "is contained by": a basin half off the edge of a zoomed-in
 *   map is still a basin the reader is looking at, and the alternative reading makes the
 *   count fall to zero the moment somebody zooms past the largest shape.
 *
 * Intersection is tested against each feature's **bounding box**, not its rings. A bbox test
 * over-counts a concave shape whose box overlaps the view while its geometry does not, and
 * that is the right error to make here: the counts are a summary of what is on screen, the
 * shapes are drawn on the same map beside them, and a reader who can see eleven polygons
 * would report "the card says 10" as the bug. Ray-casting eleven multipolygons on every
 * frame of a pan would also be the wrong use of the budget.
 */
import type { PathwaysResponse, TargetsResponse, UnitsFC, ValuesResponse } from '../../lib/api';
import type { BarsHSeries } from '../../components/charts';
import { ringsOf } from '../../lib/geo';
import type { Country } from '../../lib/state';

/** `[west, south, east, north]` — the order Leaflet's `toBBoxString` and §D5 both use. */
export type Bbox = [number, number, number, number];

/**
 * What the panel is currently scoped to. Precedence is a field rather than a convention:
 * an explicit pick beats the extent, always, because a reader who clicked a watershed and
 * then nudged the map must not watch their selection dissolve.
 */
export interface OverviewSelection {
  country: Country;
  /** `org_units.id` of a clicked or filtered watershed, or null. */
  watershed: string | null;
  /** A pathway id from the programme-area filter, or null for all of them. */
  pathway: string | null;
}

export const NO_SELECTION: OverviewSelection = { country: 'ALL', watershed: null, pathway: null };

/** Is anything explicitly picked? When true the map extent stops driving the counts. */
export function hasSelection(selection: OverviewSelection): boolean {
  return selection.country !== 'ALL' || selection.watershed !== null;
}

/**
 * Is **any** of the three filters set? — which is a different question from `hasSelection`,
 * and the Clear button asks this one.
 *
 * `hasSelection` answers "does an explicit geography beat the map extent", and a programme
 * pathway is not a geography, so it deliberately does not count there. But since R1 item 10 a
 * pathway *does* change what the map draws, and a filter a reader can set and cannot unset in
 * one click is the bug that pairing would have caused.
 */
export function hasAnyFilter(selection: OverviewSelection): boolean {
  return hasSelection(selection) || selection.pathway !== null;
}

/** A feature's bounding box, or null when it carries no usable geometry. */
export function featureBbox(geometry: unknown): Bbox | null {
  let west = Infinity;
  let south = Infinity;
  let east = -Infinity;
  let north = -Infinity;
  for (const ring of ringsOf(geometry)) {
    for (const [lng, lat] of ring) {
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) continue;
      if (lng < west) west = lng;
      if (lng > east) east = lng;
      if (lat < south) south = lat;
      if (lat > north) north = lat;
    }
  }
  return west <= east && south <= north ? [west, south, east, north] : null;
}

/** Do two boxes overlap at all? Touching edges count — a shared border is still on screen. */
export function bboxIntersects(a: Bbox, b: Bbox): boolean {
  return a[0] <= b[2] && b[0] <= a[2] && a[1] <= b[3] && b[1] <= a[3];
}

export interface CountedUnit {
  id: string;
  name: string;
  country: string | null;
  /**
   * The unit this one hangs under — `org_units.parent_id`, which for a partnership **is its
   * watershed** (`BFWS01-p1-bama` → `BFWS01`). Carried on the counted row because it is the
   * only thing that can answer "is this partnership inside the basin the reader picked",
   * and the answer is already in the payload the page loads on first paint.
   */
  parent: string | null;
}

/**
 * Does one unit belong to the current selection?
 *
 * The rule the cards and the map layer **both** apply, exported so there is one of it. A
 * selected watershed scopes to that basin *and to what hangs under it*: the basin itself
 * matches by id, its partnerships match by parent. Anything else — a neighbouring basin, a
 * partnership in another country — is out, whatever the extent says.
 *
 * Two copies of this rule is how the Partnerships card came to read 31 while the map drew a
 * different 31 and the client's checklist item 2 ("select a watershed → *its* partnerships
 * appear") failed on both.
 */
export function inSelection(
  unit: { id: string; country: string | null; parent: string | null },
  selection: OverviewSelection,
  scope: ReadonlySet<string> | null = null,
): boolean {
  // R1 item 10: a programme-pathway filter is an operational scope too, and the geography it
  // scopes to is "the watersheds that have activities in it" — `scope`. A partnership rides
  // in on its parent basin, the same way a watershed selection carries its partnerships.
  if (scope && !(scope.has(unit.id) || (unit.parent !== null && scope.has(unit.parent)))) {
    return false;
  }
  if (selection.country !== 'ALL' && unit.country && unit.country !== selection.country) {
    return false;
  }
  if (selection.watershed !== null) {
    return unit.id === selection.watershed || unit.parent === selection.watershed;
  }
  return true;
}

/**
 * The watersheds a programme-pathway filter leaves standing — `null` when no pathway is
 * picked, which is "do not scope" rather than "scope to nothing".
 *
 * R1 item 10 makes the Overview filter bar behave like the explorer's cascade: filtering to
 * an operational area removes the other areas from the map. A pathway has no geometry of its
 * own, so its area is the set of basins the server already reports activity for under that
 * id — the same `/activities/pathways` payload the chart is drawn from, so the map, the
 * cards and the bars cannot disagree about which basins the pathway touches.
 */
export function pathwayScope(
  payload: PathwaysResponse | undefined,
  pathwayId: string | null,
): Set<string> | null {
  if (!pathwayId || !payload) return null;
  const out = new Set<string>();
  for (const block of payload.watersheds) {
    if ((block.counts[pathwayId] ?? 0) > 0) out.add(block.unit_id);
  }
  return out;
}

/**
 * The units of one payload that count right now.
 *
 * `view` of `null` means "no extent to consult" — the state a map is in before its first
 * render — and everything counts, which is the programme-wide answer the page opens on
 * rather than a momentary zero while Leaflet measures its container.
 */
export function unitsInView(
  units: UnitsFC | undefined,
  view: Bbox | null,
  selection: OverviewSelection = NO_SELECTION,
  scope: ReadonlySet<string> | null = null,
): CountedUnit[] {
  const out: CountedUnit[] = [];
  for (const feature of units?.features ?? []) {
    const props = feature.properties;
    if (!props?.id) continue;
    const unit: CountedUnit = {
      id: props.id,
      name: props.name,
      country: props.country ?? null,
      parent: props.parent_id ?? null,
    };
    if (!inSelection(unit, selection, scope)) continue;
    if (!hasSelection(selection) && view) {
      const box = featureBbox(feature.geometry);
      if (box && !bboxIntersects(box, view)) continue;
    }
    out.push(unit);
  }
  return out;
}

export interface MetricCounts {
  countries: number;
  watersheds: number;
  partnerships: number;
}

/**
 * The three cards: "Total Number of Countries · Total Number of Watersheds · Total Number of
 * Partnerships — counted within the current selection/extent (programme-wide on first open:
 * 4 / 11 / 31, from the loaded boundary data)".
 *
 * Countries are counted from the **watersheds and partnerships in view**, not from the
 * country outlines: at programme zoom those two answers agree (4), and at any tighter zoom
 * only this one is right. A map framed on one basin still has three country outlines
 * clipping its corners, and a card reading "3 countries" over a single Burkinabè watershed
 * would be counting the basemap rather than the programme.
 *
 * A selected watershed narrows all three to itself and to the partnerships inside it, which
 * is the client's "select one country → all three recount for it", one level down.
 */
export function metricCounts(
  watersheds: CountedUnit[],
  partnerships: CountedUnit[],
  selection: OverviewSelection = NO_SELECTION,
): MetricCounts {
  const scoped = selection.watershed
    ? watersheds.filter((w) => w.id === selection.watershed)
    : watersheds;
  const countries = new Set<string>();
  for (const unit of [...scoped, ...partnerships]) {
    if (unit.country) countries.add(unit.country);
  }
  return {
    countries: countries.size,
    watersheds: scoped.length,
    partnerships: partnerships.length,
  };
}

export interface PathwayBars {
  labels: string[];
  series: BarsHSeries[];
  /** Non-draft activities in scope that MELS never categorised — the note under the chart. */
  uncategorised: number;
  /** The categorised registry the three bars add up to. */
  categorised: number;
}

/**
 * The pathway chart, summed over the watersheds currently counted.
 *
 * The sum is over `unitIds` rather than over the server's `totals` because the totals are a
 * programme-wide answer and this chart obeys the refresh rule. `unassigned` — activities in
 * no basin at all — is deliberately **excluded** whenever the reader is looking at a subset:
 * it belongs to no watershed, so no watershed's selection can claim it. It is added back
 * only when every basin is in view, where the chart is the whole programme and dropping 79
 * activities would make it quietly short.
 */
export function pathwayBars(
  payload: PathwaysResponse | undefined,
  unitIds: readonly string[],
  seriesLabel: string,
  pathwayFilter: string | null = null,
): PathwayBars {
  const dictionary = (payload?.pathways ?? []).filter(
    (p) => pathwayFilter === null || p.id === pathwayFilter,
  );
  const wanted = new Set(unitIds);
  const rows = (payload?.watersheds ?? []).filter((w) => wanted.has(w.unit_id));
  const whole = rows.length === (payload?.watersheds.length ?? 0) && rows.length > 0;
  const blocks = whole && payload ? [...rows, payload.unassigned] : rows;

  const totals = new Map(dictionary.map((p) => [p.id, 0]));
  let uncategorised = 0;
  for (const block of blocks) {
    for (const [id, n] of Object.entries(block.counts)) {
      if (totals.has(id)) totals.set(id, (totals.get(id) ?? 0) + n);
    }
    uncategorised += block.uncategorised;
  }

  const values = dictionary.map((p) => totals.get(p.id) ?? 0);
  return {
    labels: dictionary.map((p) => p.label),
    series: [
      {
        key: 'activities',
        label: seriesLabel,
        // One bar per pathway, each in its own pathway colour — §G8's fixed categorical
        // order, so Agroecology is the same green whichever basins are in view.
        color: dictionary[0]?.color ?? '#2E7D32',
        values,
      },
    ],
    uncategorised,
    categorised: values.reduce((sum, v) => sum + v, 0),
  };
}

/** Per-bar colours for the pathway chart — `BarsH` takes one colour per series, so the
 *  pathway chart is drawn as one series and this is what paints its rows individually. */
export function pathwayColors(payload: PathwaysResponse | undefined): string[] {
  return (payload?.pathways ?? []).map((p) => p.color);
}

export interface TargetChart {
  labels: string[];
  series: BarsHSeries[];
  /** Unit ids, index-aligned with `labels`. `null` for the programme row, which is not a
   *  unit and must not resize the map to one. */
  unitIds: (string | null)[];
  /** True while IFDC has supplied no trajectory for this indicator at all — D.2 and E.1. */
  pending: boolean;
  /**
   * The programme cumulative target, drawn as a dashed line across the country rows, or
   * `null` when the selected year is outside the trajectory. It is the *same* number as the
   * programme row's Target bar, deliberately: one commitment, drawn once as a bar on the row
   * it belongs to and once as a marker on the rows it does not.
   */
  refLine: number | null;
  /** Programme cumulative achieved for the selected year, for the insight generator. */
  programmeAchieved: number | null;
  /** Programme cumulative target for the selected year, for the insight generator. */
  programmeTarget: number | null;
}

/**
 * "Progress Against Targets": the **programme row first** with real Target and Achieved
 * bars, then one achieved-only row per country under a dashed programme reference line.
 *
 * That shape is the client's, and the reason for it is the specification's Rule 2: IFDC's
 * trajectory is a *programme-wide* commitment and none of it has been allocated to the four
 * countries. So the only row that gets a Target bar is the only row that has a target. A
 * country's row carries its achievement, an em dash where a target would be, and the line —
 * which says where the programme commitment sits without claiming a quarter of it belongs to
 * Mali. Nothing here divides, prorates or interpolates a target.
 *
 * **Both halves are cumulative.** The comparison is cumulative achieved against
 * `cumulative_target` for the selected year, and the 2025 actuals are already inside the
 * trajectory — which is why B.2.1 opens at 97,967 against 104,287 rather than at one
 * producer against 6,321.
 *
 * A year outside the trajectory (before 2026) leaves `target` null: the bars fall back to
 * achieved alone rather than drawing a 2026 commitment against a 2024 measurement.
 */
export function targetChart(
  values: ValuesResponse | undefined,
  programme: ValuesResponse | undefined,
  targets: TargetsResponse | undefined,
  names: Map<string, string>,
  labels: { target: string; achieved: string; programme: string },
  colors: { target: string; achieved: string },
): TargetChart {
  // The programme figure is only ever the programme figure: a country-scoped answer comes
  // back flagged, and taking it would put a programme commitment on a country's row.
  const programmeTarget =
    targets && !targets.country_allocation_pending && targets.scope.type === 'programme'
      ? (targets.target?.cumulative_target ?? null)
      : (targets?.target?.cumulative_target ?? null);
  const programmeAchieved = programme?.values[0]?.value ?? null;
  const countries = values?.values ?? [];

  const rowLabels: string[] = [];
  const unitIds: (string | null)[] = [];
  const targetValues: (number | null)[] = [];
  const achievedValues: (number | null)[] = [];

  // Row 0, always first, whenever the programme number has arrived. It is the row the
  // client asked to lead with and the only one a target bar may appear on.
  if (programme !== undefined || programmeTarget !== null) {
    rowLabels.push(labels.programme);
    unitIds.push(null);
    targetValues.push(programmeTarget);
    achievedValues.push(programmeAchieved);
  }
  for (const row of countries) {
    rowLabels.push(names.get(row.unit_id) ?? row.unit_id);
    unitIds.push(row.unit_id);
    targetValues.push(null);
    achievedValues.push(row.value);
  }

  return {
    labels: rowLabels,
    unitIds,
    series: [
      { key: 'target', label: labels.target, color: colors.target, values: targetValues },
      { key: 'achieved', label: labels.achieved, color: colors.achieved, values: achievedValues },
    ],
    // Only where there is something for it to run across. A chart of the programme row alone
    // would draw the line straight down its own bar.
    refLine: countries.length > 0 ? programmeTarget : null,
    programmeAchieved,
    programmeTarget,
    // The server's own flag: an absent row for *this year* is not the same sentence as an
    // indicator IFDC made no commitment for.
    pending: targets?.pending ?? true,
  };
}

/* ------------------------------------------------ target-derived insights (2026-09-09) */

/**
 * ## DORMANT since 2026-09-09 (13:00 review) — kept, not deleted
 *
 * The client's directive after the review: *"remove ALL target-derived insight statements
 * from the insight panel's list AND from the rotating bottom ticker"* — the progress, gap,
 * pace and end-of-programme sentences that the same day's specification (Rule 6) had
 * mandated. The **charts keep their real bars and their programme reference line**; only the
 * sentences go.
 *
 * So `targetInsights` and `targetPct` below are **wired to nothing**. Neither
 * `tickerInsights` nor the Overview panel calls them any more, and their English and French
 * strings — `overview.ticker.target*` and `overview.ticker.term.phase.*` — were pruned from
 * both bundles with the statements they served.
 *
 * They are kept rather than deleted because the dashboard task that follows is the surface
 * these sentences were written for, and this is the arithmetic the specification pinned:
 * the cumulative rule, the computed sign of the gap, the 0 %-awaiting case, the refusal to
 * generate at country scope. Deleting it means writing it again from the same spec.
 *
 * **Re-wiring is not just a call.** The i18n keys have to come back to both bundles first,
 * or every statement renders as its own key.
 *
 * Their unit tests stay too, and are the reason this stays correct while it sits unused.
 */

/** One indicator's numbers, as the panel already has them. Nothing here fetches. */
export interface TargetInsightInput {
  /** The contractual code, as the sentence names it — `B.2.1`. */
  code: string;
  /** Cumulative achieved at programme scope for the selected year. */
  achieved: number | null;
  targets: TargetsResponse | undefined;
}

/** `achieved / target` as whole percent, floor-guarded — never `Infinity`, never `NaN`. */
export function targetPct(achieved: number | null, target: number | null): number | null {
  if (target === null || !Number.isFinite(target) || target <= 0) return null;
  if (achieved === null || !Number.isFinite(achieved)) return 0;
  return Math.round((achieved / target) * 100);
}

/**
 * The statements the ticker rotates and the panel lists, computed from the loaded trajectory.
 *
 * Templates over the table, never fixed prose — every number in every line comes from
 * `/indicators/{id}/targets` and `/indicators/{id}/values`, which is what makes a line
 * followable: the reader can turn to the chart above and find it.
 *
 * Five kinds, in the specification's own order:
 *
 * 1. **progress** — where the indicator stands against this year's cumulative target;
 * 2. **gap or surplus** — the distance to it, with the programme phase as the explanatory
 *    clause. The sign decides which sentence: C.2.1 opens 4,490 hectares *short*, and the
 *    generator computes that rather than assuming either direction;
 * 3. **end of programme** — how far along the 2033 ambition is;
 * 4. **pace** — what closing this year's gap would take, while there is a gap to close.
 *
 * **Nothing is generated at country scope.** A programme commitment stated under a Mali
 * filter would read as a statement about Mali, which is the invented country target Rule 2
 * forbids — so a filtered panel keeps its other insights and loses these.
 *
 * **An outcome indicator with a target and no results is 0 %, not an error.** B.1.x, C.1.x
 * and D.1 have commitments and no achieved values yet; the awaiting sentence is what they
 * get, and it says so.
 */
export function targetInsights(
  inputs: readonly TargetInsightInput[],
  fmt: (value: number) => string,
  countryFiltered = false,
): TickerInsight[] {
  if (countryFiltered) return [];
  const out: TickerInsight[] = [];
  for (const { code, achieved, targets } of inputs) {
    // D.2 and E.1: no commitment in the delivery, so no sentence about one.
    if (!targets || targets.pending) continue;
    if (targets.country_allocation_pending) continue;
    const row = targets.target;
    const phase = row?.phase ?? null;
    const clause = phase ? { phase: `phase.${phase}` } : undefined;

    if (row) {
      const target = row.cumulative_target;
      const pct = targetPct(achieved, target) ?? 0;
      if (achieved === null) {
        // The outcome indicators. "0 % — awaiting first results", never a blank and never a
        // division by a measurement that does not exist.
        out.push({
          key: 'targetAwaiting',
          params: { code, year: row.year, target: fmt(target), pct: 0 },
          ...(clause ? { terms: clause } : {}),
        });
      } else {
        out.push({
          key: 'targetProgress',
          params: {
            code,
            year: row.year,
            achieved: fmt(achieved),
            target: fmt(target),
            pct,
          },
        });
        const gap = target - achieved;
        // The sign is computed, not assumed: whether an indicator opens short of its target
        // or ahead of it is a fact about the two numbers.
        out.push({
          key: gap > 0 ? 'targetGap' : gap < 0 ? 'targetSurplus' : 'targetMet',
          params: { code, year: row.year, amount: fmt(Math.abs(gap)) },
          ...(clause ? { terms: clause } : {}),
        });
        if (gap > 0) {
          out.push({
            key: 'targetPace',
            params: { code, year: row.year, remaining: fmt(gap) },
          });
        }
      }
    }

    const eop = targets.eop;
    if (eop && achieved !== null) {
      out.push({
        key: 'targetEop',
        params: {
          code,
          year: eop.year,
          eop: fmt(eop.cumulative_target),
          pct: targetPct(achieved, eop.cumulative_target) ?? 0,
        },
      });
    }
  }
  return out;
}

export interface ActivityExtremes {
  most: string | null;
  least: string | null;
}

/**
 * "Highlight the watersheds with the greatest and least activity concentrations."
 *
 * Ties break on the **highest unit id**, deterministically, so two basins on the same count
 * do not swap the badge between renders. Basins with no activity are eligible to be the
 * least active — that is a fact about the programme, not a missing value — but a scope where
 * *everything* is zero has no extremes worth drawing, and a scope of one basin has no
 * contrast to draw them with, so both answer `null` rather than badging the only shape on
 * screen as simultaneously the busiest and the quietest.
 */
export function activityExtremes(
  rows: readonly { unit_id: string; count: number }[],
): ActivityExtremes {
  if (rows.length < 2) return { most: null, least: null };
  const total = rows.reduce((sum, r) => sum + r.count, 0);
  if (total === 0) return { most: null, least: null };
  const pick = (best: (a: number, b: number) => boolean) =>
    rows.reduce((winner, row) =>
      best(row.count, winner.count) || (row.count === winner.count && row.unit_id > winner.unit_id)
        ? row
        : winner,
    ).unit_id;
  const most = pick((a, b) => a > b);
  const least = pick((a, b) => a < b);
  // A scope where every basin carries the same count has a most and a least that are the
  // same shape; badging one polygon both ways says nothing, so it says nothing.
  return most === least ? { most: null, least: null } : { most, least };
}

/**
 * Zoom at and above which partnership boundaries are drawn.
 *
 * S2: "Partnership boundaries appear only when a watershed is selected or zoomed into —
 * never at country zoom." The programme extent frames at roughly z4.5 on this panel and a
 * single watershed at z8; 7 is the level at which one basin fills the map and its
 * partnerships are separable shapes rather than a stack of slivers. Below it they are
 * hidden, whatever the reader has hovered — the rule is about what the map can usefully
 * show, not about intent.
 */
export const PARTNERSHIP_ZOOM = 7;

/** Should partnerships be on the map right now? A selection overrides the zoom. */
export function partnershipsVisible(zoom: number, selection: OverviewSelection): boolean {
  return selection.watershed !== null || zoom >= PARTNERSHIP_ZOOM;
}

/* ------------------------------------------- R1 item 5: the widget's remembered position */

/**
 * Where the "Activities by watershed" widget was left, in the map slot's own coordinates.
 *
 * **`sessionStorage`, not `localStorage`** — the client asked for the position to persist,
 * and the session is the lifetime everything else about this overlay already has (the splash
 * shows once per session; the gate is passed once per session). A widget that came back where
 * a reader dropped it three weeks ago would be a surprise, not a memory.
 */
export const WIDGET_POSITION_KEY = 'svg.overview.wswidget';

/**
 * Parse a stored position, or `null`.
 *
 * Everything that is not a pair of finite numbers is `null`: a cleared store, a truncated
 * write, a value from a future version of this widget, or anything a page on the same origin
 * decided to put under this key. The caller's fallback is the CSS anchor, which is a correct
 * position, so there is never a reason to trust a doubtful one.
 */
export function readStoredPosition(raw: string | null): { left: number; top: number } | null {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const { left, top } = parsed as { left?: unknown; top?: unknown };
    if (typeof left !== 'number' || typeof top !== 'number') return null;
    if (!Number.isFinite(left) || !Number.isFinite(top)) return null;
    return { left, top };
  } catch {
    return null;
  }
}

/* ------------------------------------------------------- R1 item 8: the ticker's insights */

/**
 * One rotating line, as a translation key plus its numbers — never as a finished sentence.
 *
 * The generator is pure and language-free on purpose: the caller holds the `t()` and the
 * locale number formatter, so the same rotation reads in English and in French without this
 * function knowing either exists.
 */
export interface TickerFragment {
  /** The key under `overview.ticker.frag.`, which carries the `_one`/`_other` forms. */
  frag: string;
  /** The raw number. It selects the plural form and nothing else — it is never displayed. */
  count: number;
  /** The same number, already formatted by the caller's `fmt` — this is what is displayed. */
  value: string;
}

export interface TickerInsight {
  /** The key under `overview.ticker.` — also the `data-insight` the tests read. */
  key: string;
  params: Record<string, string | number>;
  /**
   * Nouns the sentence does **not** itself govern, each to be pluralised on its own before
   * the sentence is composed, keyed by the placeholder it fills.
   *
   * i18next picks one plural form per `t()` call, from one `count`. A sentence with two
   * nouns therefore cannot be one call: "171 activities mapped across 1 intervention
   * watersheds" is what a single `count` produces once the second noun stops agreeing with
   * it. So the subject stays in the sentence — it also governs the verb, and in French the
   * participle — and every other noun becomes a fragment the caller pluralises first. The
   * generator still returns keys and numbers; it just returns them one noun at a time.
   */
  fragments?: Record<string, TickerFragment>;
  /**
   * Placeholders filled from a **plain** key with no plural at all — the dormant target
   * statements' phase clause ("the 2026–2028 foundation phase"). No line the ticker rotates
   * today carries one; the slot stays because the generator that fills it does.
   *
   * Separate from `fragments` because that path always passes a `count`, and i18next then
   * looks for `key_one`/`key_other` and finds nothing for a phrase that has no plural form.
   * The generator still hands over a key rather than a string, so the clause stays
   * translatable.
   */
  terms?: Record<string, string>;
}

/**
 * A statement's interpolation values, with every fragment noun pluralised on its own first.
 *
 * i18next picks one plural form per `t()` call from one `count`, so a sentence governing two
 * nouns cannot be one call (NOTES #677). This is the shared half of that: the ticker and the
 * panel both compose the same statement, and a second copy of this loop is how the two
 * surfaces come to disagree about a line the client asked to appear on both.
 */
export function insightParams(
  insight: TickerInsight,
  frag: (key: string, count: number, value: string) => string,
  term: (key: string) => string = (key) => key,
): Record<string, string | number> {
  const params: Record<string, string | number> = { ...insight.params };
  for (const [slot, fragment] of Object.entries(insight.fragments ?? {})) {
    params[slot] = frag(fragment.frag, fragment.count, fragment.value);
  }
  for (const [slot, key] of Object.entries(insight.terms ?? {})) {
    params[slot] = term(key);
  }
  return params;
}

export interface TickerInput {
  counts: MetricCounts;
  extremes: ActivityExtremes;
  /** A watershed's display name, from the loaded boundary payload. */
  name: (unitId: string) => string;
  /** Activity counts for the basins **currently in scope** — the panel's own rows. */
  activity: readonly { unit_id: string; count: number }[];
  bars: PathwayBars;
  /** `/activities/points?by=watershed`'s own "belongs to no basin" tally. */
  unassigned: number;
  /** Locale number formatting, from the caller. */
  fmt: (value: number) => string;
}

/**
 * The bottom strip's rotation — **data insights, not backend status** (client, 8 Sep 2026:
 * the ticker *"appears to currently communicate status of backend processes… we suggest this
 * communicates or highlights project or data insights"*).
 *
 * Two rules the client set, and both are structural rather than editorial:
 *
 * 1. **Every line is derived from a query the panel already runs.** Nothing here fetches:
 *    the inputs are the same memos that paint the cards, the extremes block, the pathway
 *    chart and the map key, so a reader who reads a line can turn to the panel and find the
 *    number it came from. That is also why this is a list of keys and numbers rather than of
 *    strings — a free-text line could say something the panel does not.
 * 2. **No line is invented.** Each one is emitted only when its own facts exist: no extremes
 *    in a single-basin scope, no pathway line when nothing is categorised, no "unassigned"
 *    line when the registry has none.
 *
 * **No target statement rotates here** — client directive, 2026-09-09, after the 13:00
 * review. The rotation carries activities, watersheds and pathways; the target sentences the
 * same day's Rule 6 had put in it were taken back out, and this function takes no target
 * input at all, so no loaded trajectory can put one back in by accident. The generator is
 * still in this file, dormant, above.
 *
 * The lines follow the filter, because their inputs do: filter to Mali and the rotation is
 * about Mali.
 */
export function tickerInsights(input: TickerInput): TickerInsight[] {
  const { counts, extremes, name, activity, bars, unassigned, fmt } = input;
  const out: TickerInsight[] = [];
  const countOf = (unitId: string) =>
    activity.find((row) => row.unit_id === unitId)?.count ?? 0;
  /** A noun for the locale layer to pluralise on its own. See `TickerInsight.fragments`. */
  const frag = (key: string, n: number): TickerFragment => ({ frag: key, count: n, value: fmt(n) });

  if (extremes.most) {
    out.push({
      key: 'mostActive',
      params: { name: name(extremes.most), count: countOf(extremes.most), value: fmt(countOf(extremes.most)) },
    });
  }

  const total = activity.reduce((sum, row) => sum + row.count, 0);
  if (total > 0 && counts.watersheds > 0) {
    out.push({
      key: 'mapped',
      // `count` is the activities, which the sentence's own verb agrees with; the basins are
      // a second noun with a second number, so they are pluralised separately.
      params: { count: total, value: fmt(total) },
      fragments: { watersheds: frag('watersheds', counts.watersheds) },
    });
  }

  // The biggest bar of the pathway chart, by name — the reader can see it is the longest one.
  const values = bars.series[0]?.values ?? [];
  let top = -1;
  let topValue = 0;
  for (const [i, v] of values.entries()) {
    if (typeof v === 'number' && v > topValue) {
      top = i;
      topValue = v;
    }
  }
  if (top >= 0) {
    out.push({
      key: 'topPathway',
      params: { name: bars.labels[top] ?? '', count: topValue, value: fmt(topValue) },
    });
  }

  if (counts.watersheds > 0) {
    out.push({
      key: 'coverage',
      // Three nouns and three numbers, and no verb for any of them to govern: the whole
      // line is fragments, and the sentence is the two conjunctions between them.
      params: {},
      fragments: {
        watersheds: frag('watersheds', counts.watersheds),
        partnerships: frag('partnerships', counts.partnerships),
        countries: frag('countries', counts.countries),
      },
    });
  }

  if (extremes.least) {
    out.push({
      key: 'leastActive',
      params: { name: name(extremes.least), count: countOf(extremes.least), value: fmt(countOf(extremes.least)) },
    });
  }

  if (bars.uncategorised > 0) {
    out.push({
      key: 'uncategorised',
      // The categorised denominator is its own number, and in French its participle agrees
      // with it — "pour 1 catégorisée" — so it cannot ride on the subject's `count`.
      params: { count: bars.uncategorised, value: fmt(bars.uncategorised) },
      fragments: { total: frag('categorised', bars.categorised) },
    });
  }

  if (unassigned > 0) {
    out.push({
      key: 'unassigned',
      params: { count: unassigned, value: fmt(unassigned) },
    });
  }

  return out;
}
